/**
 * Error handling utilities for Portfolio Tracker.
 *
 * Provides standardized error parsing and user-friendly error messages.
 */

export interface ApiError {
  message: string;
  statusCode?: number;
  details?: unknown;
}

/**
 * Parse API error response into a standardized format.
 */
export function parseApiError(error: unknown): ApiError {
  if (error instanceof Error) {
    return {
      message: error.message,
      details: error,
    };
  }

  if (typeof error === 'object' && error !== null) {
    const err = error as Record<string, unknown>;
    return {
      message: (err['message'] as string) || 'An unknown error occurred',
      statusCode: err['statusCode'] as number | undefined,
      details: err['details'],
    };
  }

  if (typeof error === 'string') {
    return {
      message: error,
    };
  }

  return {
    message: 'An unknown error occurred',
    details: error,
  };
}

/**
 * Get user-friendly error message for common API errors.
 */
export function getUserFriendlyError(error: ApiError): string {
  const { message, statusCode } = error;

  // Handle common HTTP status codes
  if (statusCode) {
    switch (statusCode) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'You need to log in to perform this action.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return message || 'This record already exists.';
      case 422:
        return 'Invalid data provided. Please check your input.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'Server error. Please try again later.';
      case 503:
        return 'Service unavailable. Please try again later.';
    }
  }

  // Handle common error messages
  if (message) {
    if (message.toLowerCase().includes('network')) {
      return 'Network error. Please check your internet connection.';
    }
    if (message.toLowerCase().includes('timeout')) {
      return 'Request timeout. Please try again.';
    }
    return message;
  }

  return 'An unexpected error occurred. Please try again.';
}

/**
 * Log error for debugging (in development).
 */
export function logError(error: unknown, context?: string): void {
  if (__DEV__) {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` [${context}]` : '';

    console.error(`[${timestamp}]${contextStr} Error:`);

    if (error instanceof Error) {
      console.error(`  Message: ${error.message}`);
      console.error(`  Stack: ${error.stack}`);
    } else if (typeof error === 'object' && error !== null) {
      console.error(`  Details:`, JSON.stringify(error, null, 2));
    } else {
      console.error(`  Details:`, error);
    }
  }
}

/**
 * Log API request for debugging (in development).
 */
export function logApiRequest(method: string, url: string, data?: unknown): void {
  if (__DEV__) {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] API Request: ${method} ${url}`);
    if (data) {
      console.log('  Request body:', JSON.stringify(data, null, 2));
    }
  }
}

/**
 * Log API response for debugging (in development).
 */
export function logApiResponse(
  method: string,
  url: string,
  status: number,
  duration?: number
): void {
  if (__DEV__) {
    const timestamp = new Date().toISOString();
    const durationStr = duration ? ` (${duration}ms)` : '';
    console.log(`[${timestamp}] API Response: ${method} ${url} - ${status}${durationStr}`);
  }
}

/**
 * Log API error for debugging (in development).
 */
export function logApiError(method: string, url: string, error: unknown, duration?: number): void {
  if (__DEV__) {
    const timestamp = new Date().toISOString();
    const durationStr = duration ? ` (${duration}ms)` : '';
    console.error(`[${timestamp}] API Error: ${method} ${url}${durationStr}`);

    if (error instanceof Error) {
      console.error(`  Message: ${error.message}`);
      if (error.stack) {
        console.error(`  Stack: ${error.stack}`);
      }
    } else if (typeof error === 'object' && error !== null) {
      console.error(`  Details:`, JSON.stringify(error, null, 2));
    } else {
      console.error(`  Details:`, error);
    }
  }
}
